import { useState } from 'react'
import HeaderSection from '../components/Shared/HeaderSection'

/**
 * Centro de Ayuda.
 *
 * Guía de usuario integrada en la aplicación. Explica, paso a paso, cómo
 * iniciar sesión, crear un ticket, ver y dar seguimiento a los tickets
 * propios y responde las preguntas más frecuentes.
 *
 * @component
 * @returns {JSX.Element}
 */
const HelpCenter = () => {
  const [activeTab, setActiveTab] = useState('inicio')

  const tabs = [
    { id: 'inicio', label: 'Primeros pasos', icon: 'fa-circle-info' },
    { id: 'crear', label: 'Crear un ticket', icon: 'fa-plus' },
    { id: 'mis-tickets', label: 'Ver mis tickets', icon: 'fa-list-check' },
    { id: 'ticket', label: 'Estructura del ticket', icon: 'fa-ticket' },
    { id: 'faq', label: 'Preguntas frecuentes', icon: 'fa-circle-question' },
  ]

  return (
    <div className="container-fluid py-4">
      <HeaderSection
        title="Centro de Ayuda"
        subtitle="Guía de uso: cómo crear un ticket y dar seguimiento a tus incidencias"
        icon="fa-solid fa-headset"
      />

      <div className="row mt-4">
        {/* NAV VERTICAL */}
        <div className="col-md-3 col-lg-2 mb-3">
          <div className="nav flex-column nav-pills" role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`nav-link text-start ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={`fa-solid ${tab.icon} me-2`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* CONTENIDO */}
        <div className="col-md-9 col-lg-10">
          <div className="card shadow-sm border-0">
            <div className="card-body p-4">
              {activeTab === 'inicio' && <StartGuide />}
              {activeTab === 'crear' && <CreateGuide />}
              {activeTab === 'mis-tickets' && <MyTicketsGuide />}
              {activeTab === 'ticket' && <TicketStructureGuide />}
              {activeTab === 'faq' && <Faq />}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Secciones                                                          */
/* ------------------------------------------------------------------ */

const StartGuide = () => (
  <>
    <h5 className="fw-bold mb-3">
      <i className="fa-solid fa-circle-info text-primary me-2"></i>
      Primeros pasos
    </h5>
    <p className="text-muted">
      Bienvenido al <strong>Sistema de Tickets de Grupo Mecsa</strong>. Esta
      guía te explica cómo reportar una incidencia y dar seguimiento a tus
      tickets.
    </p>

    <h6 className="fw-bold mt-4">Iniciar sesión</h6>
    <ol className="text-muted ps-3">
      <li>Abre la aplicación en tu navegador.</li>
      <li>
        En la pantalla <strong>"Sistema de Tickets"</strong> escribe tu{' '}
        <strong>Email</strong> corporativo y tu <strong>Password</strong>.
      </li>
      <li>
        Presiona <strong>Entrar</strong>. Verás el mensaje <em>"Bienvenido"</em>{' '}
        y entrarás al <strong>Inicio</strong>.
      </li>
    </ol>

    <div className="alert alert-light border d-flex align-items-start mt-3">
      <i className="fa-solid fa-lightbulb text-warning me-2 mt-1"></i>
      <span className="text-muted">
        ¿Olvidaste tu contraseña? Usa el enlace{' '}
        <strong>"¿Olvidó la contraseña?"</strong> o contacta al área de soporte.
        Para salir, usa <strong>"Cerrar sesión"</strong> en la parte inferior
        del menú lateral.
      </span>
    </div>

    <h6 className="fw-bold mt-4">El menú lateral</h6>
    <p className="text-muted mb-2">
      Desde el menú lateral puedes acceder a las secciones principales:
    </p>
    <ul className="text-muted ps-3">
      <li>
        <i className="fa-solid fa-house me-2"></i>
        <strong>Inicio</strong>: resumen y métricas.
      </li>
      <li>
        <i className="fa-solid fa-plus me-2"></i>
        <strong>Nuevo Ticket</strong>: reportar una incidencia.
      </li>
      <li>
        <i className="fa-solid fa-list-check me-2"></i>
        <strong>Mis Tickets</strong>: ver y dar seguimiento a tus tickets.
      </li>
      <li>
        <i className="fa-solid fa-circle-question me-2"></i>
        <strong>Ayuda</strong>: esta guía.
      </li>
    </ul>
  </>
)

const CreateGuide = () => (
  <>
    <h5 className="fw-bold mb-3">
      <i className="fa-solid fa-plus text-primary me-2"></i>
      Crear un ticket
    </h5>
    <p className="text-muted">
      Crea un ticket cada vez que necesites reportar un problema o una
      incidencia.
    </p>

    <h6 className="fw-bold mt-4">Paso a paso</h6>
    <ol className="text-muted ps-3">
      <li>
        En el menú lateral, haz clic en <strong>Nuevo Ticket</strong>.
      </li>
      <li>
        Completa el formulario <strong>"Crear Incidencia"</strong> (ver campos
        abajo).
      </li>
      <li>
        Presiona <strong>Publicar Incidencia</strong>. Mientras se guarda verás{' '}
        <em>"Guardando..."</em>.
      </li>
      <li>
        Al finalizar, el sistema te llevará automáticamente al{' '}
        <strong>detalle del ticket</strong> recién creado.
      </li>
    </ol>

    <h6 className="fw-bold mt-4">Campos del formulario</h6>
    <div className="table-responsive">
      <table className="table table-sm align-middle">
        <thead className="table-light">
          <tr>
            <th>Campo</th>
            <th className="text-center">Obligatorio</th>
            <th>Descripción</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="fw-semibold">Título del Problema</td>
            <td className="text-center text-success">Sí</td>
            <td>
              Un resumen corto y claro. Ej:{' '}
              <em>"Error crítico en la pasarela de pagos"</em>.
            </td>
          </tr>
          <tr>
            <td className="fw-semibold">Plataforma / Origen</td>
            <td className="text-center text-success">Sí</td>
            <td>
              Sistema o equipo donde ocurre: OPS, Proyectos, Bitrix24, CMS,
              Google Workspace, Teramind o Equipos (laptop, teléfono, etc.).
            </td>
          </tr>
          <tr>
            <td className="fw-semibold">Autor</td>
            <td className="text-center text-muted">Automático</td>
            <td>Se llena solo con tu nombre. No es editable.</td>
          </tr>
          <tr>
            <td className="fw-semibold">Nivel de Prioridad</td>
            <td className="text-center text-success">Sí</td>
            <td>Qué tan urgente es: Baja, Media o Alta.</td>
          </tr>
          <tr>
            <td className="fw-semibold">Descripción Detallada</td>
            <td className="text-center text-success">Sí</td>
            <td>
              Explica qué sucedió y, de ser posible, cómo reproducir el error.
            </td>
          </tr>
          <tr>
            <td className="fw-semibold">Archivos Adjuntos</td>
            <td className="text-center text-muted">Opcional</td>
            <td>
              Arrastra o selecciona capturas de pantalla o documentos que ayuden
              a entender el problema.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div className="alert alert-light border d-flex align-items-start mt-2">
      <i className="fa-solid fa-trash text-secondary me-2 mt-1"></i>
      <span className="text-muted">
        Si quieres limpiar el formulario y empezar de nuevo, usa el botón{' '}
        <strong>Descartar</strong>.
      </span>
    </div>

    <h6 className="fw-bold mt-4">Buenas prácticas</h6>
    <ul className="text-muted ps-3">
      <li>
        <strong>Título claro</strong>: que se entienda el problema sin abrir el
        ticket.
      </li>
      <li>
        <strong>Una incidencia por ticket</strong>: no mezcles varios problemas
        en uno solo.
      </li>
      <li>
        <strong>Adjunta evidencia</strong>: una captura de pantalla acelera la
        solución.
      </li>
      <li>
        <strong>Prioridad realista</strong>: usa <strong>Alta</strong> solo
        cuando algo esté bloqueando el trabajo.
      </li>
    </ul>
  </>
)

const MyTicketsGuide = () => (
  <>
    <h5 className="fw-bold mb-3">
      <i className="fa-solid fa-list-check text-primary me-2"></i>
      Ver mis tickets
    </h5>
    <p className="text-muted">
      Aquí consultas los tickets que <strong>tú has creado</strong> y su estado
      actual.
    </p>

    <ol className="text-muted ps-3">
      <li>
        En el menú lateral, haz clic en <strong>Mis Tickets</strong>.
      </li>
      <li>
        Verás el <strong>"Listado de Incidencias"</strong> con un resumen del
        estado de tus tickets.
      </li>
    </ol>

    <h6 className="fw-bold mt-4">Dos formas de visualizar</h6>
    <ul className="text-muted ps-3">
      <li>
        <strong>Kanban</strong> (vista por defecto): tarjetas organizadas en
        columnas según su estado.
      </li>
      <li>
        <strong>Tabla</strong>: listado en formato de filas y columnas.
      </li>
    </ul>

    <h6 className="fw-bold mt-4">Estados de un ticket</h6>
    <div className="d-flex flex-wrap gap-2 mb-3">
      <span className="badge-gm bg-info-soft text-info">Pendiente</span>
      <span className="badge-gm bg-warning-soft text-warning">En Proceso</span>
      <span className="badge-gm bg-success-soft text-success">Resuelto</span>
    </div>
    <ul className="text-muted ps-3">
      <li>
        <strong>Pendiente</strong>: el ticket fue creado y está en espera de
        atención.
      </li>
      <li>
        <strong>En Proceso</strong>: alguien ya está trabajando en la solución.
      </li>
      <li>
        <strong>Resuelto</strong>: el problema fue solucionado.
      </li>
    </ul>

    <h6 className="fw-bold mt-4">Buscar y filtrar</h6>
    <ul className="text-muted ps-3">
      <li>
        <strong>Buscar</strong>: escribe en el campo{' '}
        <em>"Buscar por ID o título..."</em> para encontrar un ticket por su
        número o nombre.
      </li>
      <li>
        <strong>Filtrar por Estado</strong>: Todos los Estados, Pendiente, En
        Proceso o Resuelto.
      </li>
      <li>
        <strong>Filtrar por Origen</strong>: por la plataforma o sistema.
      </li>
      <li>
        <strong>Paginación</strong>: usa <strong>Anterior / Siguiente</strong>{' '}
        para navegar entre páginas.
      </li>
    </ul>

    <h6 className="fw-bold mt-4">Abrir un ticket</h6>
    <p className="text-muted mb-0">
      Haz clic en el icono <i className="fa-solid fa-eye"></i> (o en la tarjeta)
      para abrir el detalle del ticket. En la pestaña{' '}
      <strong>"Estructura del ticket"</strong> se explica todo lo que verás
      dentro.
    </p>
  </>
)

const TicketStructureGuide = () => (
  <>
    <h5 className="fw-bold mb-3">
      <i className="fa-solid fa-ticket text-primary me-2"></i>
      Estructura del ticket
    </h5>
    <p className="text-muted">
      Al abrir un ticket verás su detalle dividido en dos zonas: el{' '}
      <strong>contenido principal</strong> (a la izquierda) y un{' '}
      <strong>panel lateral</strong> de gestión (a la derecha).
    </p>

    {/* ENCABEZADO */}
    <h6 className="fw-bold mt-4">Encabezado</h6>
    <p className="text-muted mb-2">En la parte superior se muestra:</p>
    <ul className="text-muted ps-3">
      <li>
        El <strong>título</strong> del ticket.
      </li>
      <li>
        Una etiqueta con el <strong>número de ticket</strong> (ej.{' '}
        <span className="badge rounded-pill bg-primary">#123</span>) y la{' '}
        <strong>fecha de creación</strong>.
      </li>
      <li>
        La etiqueta{' '}
        <span className="badge rounded-pill bg-success-subtle text-success border border-success">
          En curso
        </span>{' '}
        cuando el cronómetro de atención está activo.
      </li>
      <li>
        Los botones <strong>Imprimir</strong> (genera una versión para
        impresión) y <strong>Compartir</strong> (copia el enlace del ticket; el
        botón cambia a <em>"Copiado"</em>).
      </li>
    </ul>

    {/* CONTENIDO PRINCIPAL */}
    <h6 className="fw-bold mt-4">Contenido principal</h6>
    <div className="table-responsive">
      <table className="table table-sm align-middle">
        <thead className="table-light">
          <tr>
            <th>Sección</th>
            <th>Qué contiene</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="fw-semibold">Descripción</td>
            <td>El detalle completo de la incidencia, tal como se reportó.</td>
          </tr>
          <tr>
            <td className="fw-semibold">Archivos</td>
            <td>
              Los adjuntos del ticket (capturas, documentos). Puedes abrirlos o
              descargarlos.
            </td>
          </tr>
          <tr>
            <td className="fw-semibold">Soluciones</td>
            <td>
              Las soluciones registradas para resolver el problema. Con el botón{' '}
              <i className="fa-solid fa-lightbulb"></i> se puede{' '}
              <strong>Agregar Solución</strong> (disponible mientras el ticket
              no esté completado).
            </td>
          </tr>
          <tr>
            <td className="fw-semibold">Comentarios</td>
            <td>
              Conversación y seguimiento en orden cronológico. Puedes escribir y{' '}
              <strong>agregar un comentario</strong>.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    {/* PANEL LATERAL */}
    <h6 className="fw-bold mt-4">Panel lateral (gestión)</h6>
    <div className="table-responsive">
      <table className="table table-sm align-middle">
        <thead className="table-light">
          <tr>
            <th>Bloque</th>
            <th>Para qué sirve</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="fw-semibold">Etapa</td>
            <td>
              Muestra y permite cambiar el <strong>estado</strong> del ticket
              (Pendiente, En Proceso, Resuelto). Al cambiarlo se notifica al
              creador.
            </td>
          </tr>
          <tr>
            <td className="fw-semibold">Tiempo</td>
            <td>
              Cronómetro de atención. Permite <strong>iniciar</strong> y{' '}
              <strong>detener</strong> el conteo del tiempo dedicado al ticket.
            </td>
          </tr>
          <tr>
            <td className="fw-semibold">Responsable</td>
            <td>
              La persona asignada para resolverlo. Con <strong>Cambiar</strong>{' '}
              se reasigna a otro empleado. Incluye la opción{' '}
              <strong>Marcar como completado</strong>.
            </td>
          </tr>
          <tr>
            <td className="fw-semibold">Creador</td>
            <td>Quién abrió el ticket.</td>
          </tr>
          <tr>
            <td className="fw-semibold">Etiquetas</td>
            <td>
              Etiquetas para clasificar el ticket. Con{' '}
              <i className="fa-solid fa-plus"></i> se agregan o gestionan.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div className="alert alert-light border d-flex align-items-start mt-2">
      <i className="fa-solid fa-circle-check text-success me-2 mt-1"></i>
      <span className="text-muted">
        Al <strong>marcar el ticket como completado</strong> se detiene el
        cronómetro y se envía una notificación al creador. Mientras el ticket
        está completado, no se pueden agregar nuevas soluciones.
      </span>
    </div>
  </>
)

const Faq = () => {
  const items = [
    {
      q: '¿Quién puede ver mis tickets?',
      a: 'En Mis Tickets ves los tickets que tú creaste. El equipo de soporte puede verlos para darles seguimiento.',
    },
    {
      q: '¿Puedo editar un ticket después de crearlo?',
      a: 'Puedes agregar comentarios y archivos para complementar la información. El estado y el responsable los gestiona el equipo de soporte.',
    },
    {
      q: '¿Cómo sé que ya resolvieron mi ticket?',
      a: 'El estado del ticket cambiará a "Resuelto" y podrás revisar la sección de Soluciones.',
    },
    {
      q: '¿Qué prioridad debo elegir?',
      a: 'Alta: bloquea tu trabajo o afecta a varias personas. Media: te afecta pero puedes seguir trabajando. Baja: una mejora o algo no urgente.',
    },
  ]

  return (
    <>
      <h5 className="fw-bold mb-3">
        <i className="fa-solid fa-circle-question text-primary me-2"></i>
        Preguntas frecuentes
      </h5>

      <div className="accordion" id="faqAccordion">
        {items.map((item, i) => (
          <div className="accordion-item" key={i}>
            <h2 className="accordion-header">
              <button
                className="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target={`#faq-${i}`}
              >
                {item.q}
              </button>
            </h2>
            <div
              id={`faq-${i}`}
              className="accordion-collapse collapse"
              data-bs-parent="#faqAccordion"
            >
              <div className="accordion-body text-muted">{item.a}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="alert alert-light border d-flex align-items-start mt-4">
        <i className="fa-solid fa-headset text-primary me-2 mt-1"></i>
        <span className="text-muted">
          Si necesitas ayuda adicional, contacta al equipo de soporte.
        </span>
      </div>
    </>
  )
}

export default HelpCenter
